import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import VoiceRecorder from "@/components/VoiceRecorder";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Activity, Clock } from "lucide-react";
import { toast } from "sonner";

export const BRISTOL = [
  { type: 1, label: "Separate hard lumps", tag: "Constipated", color: "text-destructive" },
  { type: 2, label: "Lumpy sausage", tag: "Constipated", color: "text-destructive" },
  { type: 3, label: "Sausage with cracks", tag: "Normal", color: "text-secondary" },
  { type: 4, label: "Smooth soft sausage", tag: "Ideal", color: "text-secondary" },
  { type: 5, label: "Soft blobs", tag: "Lacking fiber", color: "text-accent-foreground" },
  { type: 6, label: "Mushy, ragged edges", tag: "Loose", color: "text-primary" },
  { type: 7, label: "Watery, liquid", tag: "Diarrhea", color: "text-destructive" },
];

const meta = (t) => BRISTOL.find((b) => b.type === t);

export default function GutTracker() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/bowel/today");
    setData(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveForm = async () => {
    if (!selected) return toast.error("Pick a Bristol type first");
    setSaving(true);
    try {
      await api.post("/bowel/log", { bristol_type: selected, notes });
      toast.success("Logged");
      setSelected(null);
      setNotes("");
      load();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bowel/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="fade-up">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Gut health</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black mt-1">Bowel tracker</h1>
          <p className="text-muted-foreground mt-1">Log by voice, or pick a type on the Bristol scale.</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 flex flex-col items-center fade-up">
          <VoiceRecorder
            onLogged={load}
            voiceUrl="/bowel/log-voice"
            textUrl="/bowel/log-text"
            textKey="text"
            placeholder='e.g. "type 4, felt normal, no strain"'
            idleLabel="Tap & describe your movement"
          />
        </div>

        {/* Bristol scale form */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 fade-up" data-testid="bristol-form">
          <h2 className="font-heading text-xl font-bold mb-1">Or log manually</h2>
          <p className="text-muted-foreground text-sm mb-5">Bristol Stool Scale — tap the closest match.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {BRISTOL.map((b) => (
              <button
                key={b.type}
                onClick={() => setSelected(b.type)}
                className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${selected === b.type ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                data-testid={`bristol-type-${b.type}`}
              >
                <span className={`w-9 h-9 rounded-lg bg-muted flex items-center justify-center font-heading font-black ${b.color}`}>{b.type}</span>
                <span className="min-w-0">
                  <span className="block font-semibold text-sm truncate">{b.label}</span>
                  <span className={`text-xs font-bold ${b.color}`}>{b.tag}</span>
                </span>
              </button>
            ))}
          </div>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (bloating, urgency, color…)" className="rounded-xl mb-4" data-testid="bowel-notes-input" />
          <Button onClick={saveForm} disabled={saving} className="rounded-full font-bold w-full h-11" data-testid="bowel-save-btn">Save entry</Button>
        </div>

        {/* Today list */}
        <div className="fade-up">
          <h2 className="font-heading text-xl font-bold mb-4">Today · {data?.count ?? 0} logged</h2>
          {data && data.logs.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center text-muted-foreground" data-testid="bowel-empty">
              <Activity className="w-8 h-8 mx-auto mb-3 text-secondary" />
              Nothing logged today.
            </div>
          ) : (
            <div className="space-y-3">
              {data?.logs.map((log) => {
                const m = meta(log.bristol_type);
                return (
                  <div key={log.id} className="bg-card border border-border rounded-2xl p-5 flex items-start justify-between gap-4" data-testid={`bowel-item-${log.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {log.bristol_type && <span className={`w-7 h-7 rounded-lg bg-muted flex items-center justify-center font-heading font-black text-sm ${m?.color}`}>{log.bristol_type}</span>}
                        <span className="font-semibold">{m ? m.label : "Logged"}</span>
                        {log.consistency && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground capitalize">{log.consistency}</span>}
                      </div>
                      {log.notes && <p className="text-sm text-muted-foreground mt-2">{log.notes}</p>}
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(log.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                    <button onClick={() => remove(log.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0" data-testid={`delete-bowel-${log.id}`}>
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
